import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Users, ImageOff, ShieldCheck, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function PlayerTile({ player }) {
  return (
    <div className="flex flex-col items-center gap-1.5 p-2 rounded-lg hover:bg-slate-50 transition-colors" data-testid="public-player-tile">
      {player.headshot_path ? (
        <img
          src={`${API_URL}/api/files/${player.headshot_path}`}
          alt={player.name}
          className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover ring-2 ring-[#00247D]/15 shadow-sm"
        />
      ) : (
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 ring-2 ring-slate-300 flex items-center justify-center text-slate-400 text-base font-bold">
          {player.jersey_number || (player.name || '').slice(0, 1).toUpperCase()}
        </div>
      )}
      <div className="text-center min-w-0 max-w-[6.5rem]">
        <div className="text-[11px] font-mono text-slate-500">#{player.jersey_number || '–'}</div>
        <div className="text-xs font-semibold text-slate-800 truncate">{player.name}</div>
      </div>
    </div>
  );
}

function TeamCard({ team, onClick }) {
  const playerCount = team.players?.length || 0;
  return (
    <Card
      className="overflow-hidden border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
      onClick={onClick}
      data-testid={`public-team-card-${team.team_id}`}>
      {/* Cover photo or placeholder */}
      <div className="relative h-32 sm:h-40 bg-gradient-to-br from-[#00247D] to-[#001A5C]">
        {team.team_photo ? (
          <img
            src={`${API_URL}/api/files/${team.team_photo}`}
            alt={`${team.team_name} team photo`}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/30">
            <ImageOff className="w-10 h-10" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-amber-400 text-black text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider flex items-center gap-1 shadow-md">
          <BarChart3 className="w-3 h-3" /> View Stats
        </div>
        <div className="absolute inset-x-0 bottom-0 p-4 flex items-end gap-3">
          {team.team_logo && (
            <img
              src={`${API_URL}/api/files/${team.team_logo}`}
              alt={`${team.team_name} logo`}
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover bg-white p-1 ring-2 ring-white/50 shadow-md"
            />
          )}
          <div className="min-w-0 flex-1">
            <h3 className="text-white text-lg sm:text-xl font-black uppercase tracking-tight leading-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              {team.team_name}
            </h3>
            <div className="flex items-center gap-1.5 text-white/80 text-[11px] font-medium">
              <ShieldCheck className="w-3 h-3" />
              <span>Roster Submitted</span>
              <span className="text-white/50">·</span>
              <span>{playerCount} player{playerCount === 1 ? '' : 's'}</span>
            </div>
          </div>
        </div>
      </div>

      <CardContent className="p-4">
        {playerCount === 0 ? (
          <p className="text-sm text-slate-500 text-center">No players listed</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1">
            {team.players.map((p, i) => (
              <PlayerTile key={i} player={p} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function TeamRosterCards({ rosters, onTeamClick }) {
  if (!rosters || rosters.length === 0) {
    return (
      <Card className="border-dashed border-slate-300 bg-slate-50" data-testid="public-teams-empty">
        <CardContent className="py-12 text-center">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold mb-1">No team rosters published yet</p>
          <p className="text-slate-500 text-sm">Team rosters appear here once contacts have submitted them.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-testid="public-team-cards-grid">
      {rosters.map((team) => (
        <TeamCard key={team.team_id} team={team} onClick={() => onTeamClick && onTeamClick(team)} />
      ))}
    </div>
  );
}
